import React, { useEffect, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, Modal, ScrollView, StyleSheet, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from './DirectionalIcon';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { colors, spacing, typography } from '../theme';
import { useLocale } from '../contexts/LocaleContext';

interface ActiveJob {
  jobId: string;
  status: 'processing' | 'paused';
  totalRows: number;
  processedCount: number;
  processingTotal: number | null;
  createdAt: string;
}

const POLL_INTERVAL_MS = 3000;

/**
 * Global "bulk contacts upload" progress indicator — polls for every
 * still-processing job on this business (not just one remembered locally),
 * so it stays accurate regardless of which screen/device started an upload,
 * and survives navigating away from the upload screen entirely (the
 * concrete gap this was built to close: a job's only state used to live in
 * BulkContactUploadScreen's own local variables, so leaving that screen
 * meant losing all visibility into a still-running import).
 *
 * Shows one aggregate bar — weighted by actual contact counts across every
 * running job, not a plain average of percentages, so a 10-contact job
 * finishing doesn't move the needle as much as a 1000-contact one — and a
 * tap opens a modal listing each job's own individual progress.
 */
export const BulkUploadProgressIndicator: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { showNotification } = useAppContext();
  const { t } = useLocale();
  const [jobs, setJobs] = useState<ActiveJob[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [actionLoadingJobId, setActionLoadingJobId] = useState<string | null>(null);
  const hadJobsRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      try {
        const res = await apiService.getActiveBulkContactUploadJobs();
        if (cancelled) return;
        const nextJobs = res.statusCode === 1000 ? (res.data?.jobs || []) : [];
        if (hadJobsRef.current && nextJobs.length === 0) {
          // Every job that was running has finished (or failed) since the last
          // poll — the originating screen (if still open) already shows its
          // own result notification, so this only fires for jobs nobody is
          // actively watching anymore.
          showNotification('success', t('contacts.bulkUploadProgress.doneTitle'), t('contacts.bulkUploadProgress.doneMessage'));
        }
        hadJobsRef.current = nextJobs.length > 0;
        setJobs(nextJobs);
      } catch {
        // Transient network hiccup — next poll will retry; don't flicker the
        // indicator away over one failed request.
      } finally {
        if (!cancelled) timer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (jobs.length === 0) return null;

  // Optimistic local update — the next 3s poll will reconcile with the
  // server either way, but waiting for it would make every tap feel
  // sluggish for no reason.
  const handlePause = async (jobId: string) => {
    setActionLoadingJobId(jobId);
    try {
      await apiService.pauseBulkContactUpload(jobId);
      setJobs((prev) => prev.map((j) => (j.jobId === jobId ? { ...j, status: 'paused' } : j)));
    } catch {
      Alert.alert(t('contacts.bulkUploadProgress.actionErrorTitle'), t('contacts.bulkUploadProgress.pauseFailed'));
    } finally {
      setActionLoadingJobId(null);
    }
  };

  const handleResume = async (jobId: string) => {
    setActionLoadingJobId(jobId);
    try {
      await apiService.resumeBulkContactUpload(jobId);
      setJobs((prev) => prev.map((j) => (j.jobId === jobId ? { ...j, status: 'processing' } : j)));
    } catch {
      Alert.alert(t('contacts.bulkUploadProgress.actionErrorTitle'), t('contacts.bulkUploadProgress.resumeFailed'));
    } finally {
      setActionLoadingJobId(null);
    }
  };

  const handleCancel = (jobId: string) => {
    Alert.alert(
      t('contacts.bulkUploadProgress.cancelConfirmTitle'),
      t('contacts.bulkUploadProgress.cancelConfirmMessage'),
      [
        { text: t('contacts.bulkUploadProgress.cancelConfirmDismiss'), style: 'cancel' },
        {
          text: t('contacts.bulkUploadProgress.cancelConfirmAction'),
          style: 'destructive',
          onPress: async () => {
            setActionLoadingJobId(jobId);
            try {
              await apiService.cancelBulkContactUpload(jobId);
              setJobs((prev) => prev.filter((j) => j.jobId !== jobId));
            } catch {
              Alert.alert(t('contacts.bulkUploadProgress.actionErrorTitle'), t('contacts.bulkUploadProgress.cancelFailed'));
            } finally {
              setActionLoadingJobId(null);
            }
          },
        },
      ]
    );
  };

  const totalProcessed = jobs.reduce((sum, j) => sum + j.processedCount, 0);
  const totalExpected = jobs.reduce((sum, j) => sum + (j.processingTotal ?? j.totalRows), 0);
  const aggregatePercent = totalExpected > 0 ? Math.min(99, Math.round((totalProcessed / totalExpected) * 100)) : 0;

  return (
    <>
      <TouchableOpacity style={styles.bar} onPress={() => setShowModal(true)} activeOpacity={0.7}>
        <Ionicons name="cloud-upload-outline" size={16} color={colors.primary} />
        <View style={styles.barTextColumn}>
          <Text style={styles.barTitle}>
            {jobs.length > 1
              ? t('contacts.bulkUploadProgress.multipleTitle', { count: jobs.length })
              : t('contacts.bulkUploadProgress.singleTitle')}
          </Text>
          <Text style={styles.barSubtitle}>
            {t('contacts.bulkUploadProgress.countLabel', { processed: totalProcessed, total: totalExpected })}
          </Text>
        </View>
        <View style={styles.barProgressTrack}>
          <View style={[styles.barProgressFill, { width: `${aggregatePercent}%` }]} />
        </View>
        <Text style={styles.barPercent}>{aggregatePercent}%</Text>
        <DirectionalIcon name="chevron-forward" size={16} color={colors.text.secondary} />
      </TouchableOpacity>

      <Modal visible={showModal} transparent animationType="fade" onRequestClose={() => setShowModal(false)}>
        <TouchableOpacity style={styles.modalBackdrop} activeOpacity={1} onPress={() => setShowModal(false)}>
          <TouchableOpacity activeOpacity={1} onPress={(e) => e.stopPropagation()} style={styles.modalContent}>
            <Text style={styles.modalTitle}>{t('contacts.bulkUploadProgress.modalTitle')}</Text>
            <ScrollView style={styles.modalList}>
              {jobs.map((job) => {
                const expected = job.processingTotal ?? job.totalRows;
                const percent = expected > 0 ? Math.min(99, Math.round((job.processedCount / expected) * 100)) : 0;
                const isPaused = job.status === 'paused';
                const isBusy = actionLoadingJobId === job.jobId;
                return (
                  <View key={job.jobId} style={styles.jobRow}>
                    <View style={styles.jobRowHeader}>
                      <Text style={styles.jobRowLabel}>
                        {t('contacts.bulkUploadProgress.countLabel', { processed: job.processedCount, total: expected })}
                        {isPaused ? ` · ${t('contacts.bulkUploadProgress.pausedBadge')}` : ''}
                      </Text>
                      <Text style={styles.jobRowPercent}>{percent}%</Text>
                    </View>
                    <View style={styles.barProgressTrack}>
                      <View style={[styles.barProgressFill, { width: `${percent}%` }, isPaused && styles.barProgressFillPaused]} />
                    </View>
                    <View style={styles.jobRowActions}>
                      {isPaused ? (
                        <TouchableOpacity
                          style={styles.jobActionButton}
                          onPress={() => handleResume(job.jobId)}
                          disabled={isBusy}
                        >
                          <Ionicons name="play" size={14} color={colors.primary} />
                          <Text style={styles.jobActionButtonText}>{t('contacts.bulkUploadProgress.resume')}</Text>
                        </TouchableOpacity>
                      ) : (
                        <TouchableOpacity
                          style={styles.jobActionButton}
                          onPress={() => handlePause(job.jobId)}
                          disabled={isBusy}
                        >
                          <Ionicons name="pause" size={14} color={colors.primary} />
                          <Text style={styles.jobActionButtonText}>{t('contacts.bulkUploadProgress.pause')}</Text>
                        </TouchableOpacity>
                      )}
                      <TouchableOpacity
                        style={styles.jobActionButton}
                        onPress={() => handleCancel(job.jobId)}
                        disabled={isBusy}
                      >
                        <Ionicons name="close-circle-outline" size={14} color={colors.error} />
                        <Text style={[styles.jobActionButtonText, { color: colors.error }]}>{t('contacts.bulkUploadProgress.cancel')}</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                );
              })}
            </ScrollView>
            <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowModal(false)}>
              <Text style={styles.modalCloseButtonText}>{t('contacts.bulkUploadProgress.close')}</Text>
            </TouchableOpacity>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </>
  );
};

const getStyles = () => StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background.secondary,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  barTextColumn: {
    minWidth: 80,
  },
  barTitle: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.textDark,
  },
  barSubtitle: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  barProgressTrack: {
    flex: 1,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.border,
    overflow: 'hidden',
  },
  barProgressFill: {
    height: '100%',
    borderRadius: 3,
    backgroundColor: colors.primary,
  },
  barPercent: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
    minWidth: 32,
    textAlign: 'right',
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    width: '90%',
    maxWidth: 420,
    maxHeight: '70%',
    backgroundColor: colors.background.surface,
    borderRadius: 12,
    padding: spacing.md,
  },
  modalTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
    marginBottom: spacing.sm,
  },
  modalList: {
    maxHeight: 320,
  },
  jobRow: {
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  jobRowHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: spacing.xs,
  },
  jobRowLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
  },
  jobRowPercent: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
  },
  barProgressFillPaused: {
    backgroundColor: colors.text.secondary,
  },
  jobRowActions: {
    flexDirection: 'row',
    gap: spacing.md,
    marginTop: spacing.xs,
  },
  jobActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  jobActionButtonText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  modalCloseButton: {
    marginTop: spacing.sm,
    alignItems: 'center',
    paddingVertical: spacing.sm,
  },
  modalCloseButtonText: {
    color: colors.primary,
    fontFamily: typography.fontFamily.medium,
  },
});
